import React, { useMemo, useState } from "react";
import { Col, Dropdown, DropdownButton, OverlayTrigger, Placeholder, Row, Tooltip } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import { AbsenceStatus, getAbsenceStatusColor, getAbsenceStatusIcon } from "../../services/MeetingAbsenceStatus";
import { PermissionComponent } from "../../services/PermissionGuard";
import { formatDateHumanly, isPast } from "../../services/TimeService";
import translate from "../../services/Translate";
import { completeNavigate } from "../../services/UtilityService";
import DstButton from "../DstButton/DstButton";
import Popup from "../Popup/Popup";
import {
  getAttendanceIconClass,
  getFontClass,
  openRecordLink,
  renderAttendance,
  updateAbsenceProofStatus,
} from "./MeetingBlock.function";
import "./MeetingBlock.scss";

const MeetingBlock = ({
  dt_start,
  dt_end,
  name,
  meeting_type,
  animators = [],
  mock,
  language,
  link,
  record_link,
  attended,
  id,
  absence_proof,
  refreshUserMeetings,
  maxAnimatorsDisplayed = 2,
}: any) => {
  const { user_id } = useParams();
  const [showProofModal, setShowProofModal] = useState(false);
  const [loadingApproval, setLoadingApproval] = useState(false);
  const [loadingApprovalType, setLoadingApprovalType] = useState<null | "validated" | "declined">(null);

  const navigate = useNavigate();

  const _handleUpdateStatus = (status: "validated" | "declined") => {
    setLoadingApprovalType(status);
    setLoadingApproval(true);
    updateAbsenceProofStatus(
      id,
      Number(user_id),
      status,
      language,
      () => {
        setShowProofModal(false);
        refreshUserMeetings?.();
        setLoadingApproval(false);
        setLoadingApprovalType(null);
      },
      setLoadingApproval
    );
  };

  const _getAbsenceStatusLabel = (status: AbsenceStatus): string => {
    switch (status) {
      case "validated":
        return translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.STATUS.JUSTIFICATION_VALIDATED");
      case "declined":
        return translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.STATUS.JUSTIFICATION_DECLINED");
      case "pending":
      default:
        return translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.STATUS.JUSTIFICATION_PENDING");
    }
  };

  const animatorsDisplay = useMemo(() => {
    if (animators.length === 0) return null;

    const names = animators.map((animator: { full_name: string }) => animator.full_name);

    return {
      names,
      hasOverflow: names.length > maxAnimatorsDisplayed,
      visible: names.slice(0, maxAnimatorsDisplayed),
      hiddenCount: Math.max(names.length - maxAnimatorsDisplayed, 0),
      hidden: names.slice(maxAnimatorsDisplayed),
    };
  }, [animators, maxAnimatorsDisplayed]);

  if (mock)
    return (
      <Placeholder
        animation="glow"
        className="row ms-0 meeting-block d-flex border mb-3 px-1 py-2 p-xl-3 rounded align-items-center"
      >
        <Col xl={1} lg={2} md={6} className="border-end text-center">
          <Placeholder xs={12} as="div" className="meeting-month s-regular"></Placeholder>
          <Placeholder xs={12} as="div" className="meeting-day h1"></Placeholder>
        </Col>
        <Col lg={3} md={6}>
          <Placeholder xs={12} as="div" className="meeting-time m-bold"></Placeholder>
          <Placeholder xs={12} as="div" className="meeting-attendees"></Placeholder>
        </Col>
        <Col xl={6} lg={5} md={9}>
          <Placeholder xs={12} as="div" className="meeting-type smoke-gray-font s-bold"></Placeholder>
          <Placeholder xs={12} as="div" className="meeting-title m-regular"></Placeholder>
        </Col>
        <Col lg={2} md={3} className="text-end">
          <DstButton mock btnClass="my-2" btnWidth="71px" />
        </Col>
      </Placeholder>
    );
  const isMeetingPast = isPast(dt_end);

  let monthParams: Intl.DateTimeFormatOptions = { month: "long" };
  if (new Date().getFullYear() !== dt_start.getFullYear()) {
    monthParams["year"] = "numeric";
  }

  return (
    <React.Fragment>
      <Row className="ms-0 meeting-block d-flex border mb-3 px-1 py-2 p-xl-3 rounded align-items-center w-100">
        <Col xl={1} lg={2} md={6} className={`border-end text-center ${getFontClass(isMeetingPast, attended)}`}>
          <div className="meeting-month text-capitalize s-regular">
            {dt_start.toLocaleString(language, monthParams)}
          </div>
          <div className="meeting-day h1">{dt_start.getDate()}</div>
        </Col>
        <Col lg={3} md={6} className={`d-flex align-items-${isMeetingPast ? "end" : "center"}`}>
          <div>
            <div className="meeting-time m-bold d-flex align-items-center">
              <span className="dst-icon-clock me-1" />
              {`${dt_start.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })} - ${dt_end.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}`}
            </div>
            {animatorsDisplay && (
              <div className="meeting-animators s-regular text-muted">
                <span>
                  <span className="dst-icon-users-01 me-1" />
                  {animatorsDisplay.visible.join(", ")}

                  {animatorsDisplay.hasOverflow && (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`tooltip-animators-${id}`}>{animatorsDisplay.hidden.join(", ")}</Tooltip>}
                    >
                      <span className="badge bg-secondary ms-1">+{animatorsDisplay.hiddenCount}</span>
                    </OverlayTrigger>
                  )}
                </span>
              </div>
            )}

            {absence_proof && (
              <div className={`d-flex align-items-center ${isMeetingPast ? "mt-1" : ""}`} id="absence-proof-status">
                {isMeetingPast ? (
                  <React.Fragment>
                    <span className={getAttendanceIconClass(attended)} />
                    <span className="ms-1 s-bold">{renderAttendance(isMeetingPast, attended, language)}</span>
                  </React.Fragment>
                ) : (
                  <span className={`me-2 s-regular ${getAbsenceStatusColor(absence_proof.status)}`}>
                    {_getAbsenceStatusLabel(absence_proof.status)}
                  </span>
                )}
                <div
                  className="d-inline-block position-relative mx-2"
                  onClick={() => setShowProofModal(true)}
                  role="button"
                  aria-label={translate(language, "PAGES.USER.MEETING.VIEW_ABSENCE_PROOF")}
                >
                  <i className={absence_proof.status === "pending" ? "dst-icon-alert-triangle" : "dst-icon-eye"} />
                  {absence_proof.status === "pending" && <span className="icon-pulse-effect" />}
                </div>
              </div>
            )}
          </div>
        </Col>
        <Col xl={6} lg={5} md={9}>
          <div className="meeting-type smoke-gray-font s-bold">{meeting_type}:</div>
          <div className="meeting-title m-regular">{name}</div>
        </Col>
        <Col lg={2} md={3} className="text-end">
          <DropdownButton
            className="my-2 dropdown-meeting"
            title={translate(language, "PAGES.USER.MEETING.MORE")}
            variant="primary"
          >
            <Dropdown.Item
              className="s-bold d-flex align-items-center"
              onClick={() => navigator.clipboard.writeText(id)}
            >
              <span className="me-1">{translate(language, "PAGES.USER.MEETING.BUTTON.COPY_MEETING_ID")}</span>
              <span className="dst-icon-copy-01" />
            </Dropdown.Item>
            {!isMeetingPast && link && (
              <Dropdown.Item className="s-bold" onClick={() => navigator.clipboard.writeText(link)}>
                <span className="me-1">{translate(language, "PAGES.USER.MEETING.BUTTON.COPY_LINK")}</span>
                <span className="dst-icon-copy-01" />
              </Dropdown.Item>
            )}
            {isMeetingPast && record_link && (
              <Dropdown.Item className="s-bold" onClick={() => openRecordLink(record_link, language)}>
                {translate(language, "PAGES.USER.MEETING.BUTTON.VIEW_RECORD")}
              </Dropdown.Item>
            )}
            {((!isMeetingPast && !link) || (isMeetingPast && !record_link)) && (
              <Dropdown.Item disabled className="text-muted s-bold">
                {translate(language, "PAGES.USER.MEETING.BUTTON.NO_LINK")}
              </Dropdown.Item>
            )}
            <PermissionComponent neededPermission={[{ methods: ["GET"], route: "/meetings/*" }]}>
              <Dropdown.Item
                className="s-bold d-flex align-items-center"
                onClick={(event) => completeNavigate(event, navigate, `/meeting/${id}`)}
              >
                <span className="me-1">{translate(language, "PAGES.USER.MEETING.BUTTON.GO_TO_MEETING_NEXUS")}</span>
                <span className="dst-icon-link-external-02" />
              </Dropdown.Item>
            </PermissionComponent>
          </DropdownButton>
        </Col>
      </Row>
      <Popup
        show={showProofModal}
        onClose={() => setShowProofModal(false)}
        modalTitle={translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.TITLE")}
        footerContent={
          absence_proof?.status === "pending" ? (
            <React.Fragment>
              <DstButton
                value={translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.BUTTON.APPROVE")}
                variant="success"
                clickFunction={() => _handleUpdateStatus("validated")}
                loading={loadingApproval && loadingApprovalType === "validated"}
                disabled={loadingApproval}
              />
              <DstButton
                value={translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.BUTTON.REJECT")}
                variant="danger"
                clickFunction={() => _handleUpdateStatus("declined")}
                loading={loadingApproval && loadingApprovalType === "declined"}
                disabled={loadingApproval}
              />
            </React.Fragment>
          ) : (
            <DstButton
              value={translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.BUTTON.CLOSE")}
              clickFunction={() => setShowProofModal(false)}
            />
          )
        }
      >
        {absence_proof?.filename ? (
          <div>
            <div className="d-flex align-items-center mb-3">
              <strong className="me-2">
                {translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.STATUS.TITLE")}:
              </strong>
              {getAbsenceStatusIcon(absence_proof.status)}
              <span className={`me-2 ${getAbsenceStatusColor(absence_proof.status)}`}>{absence_proof.status}</span>
              {absence_proof.validator && (
                <span className="ms-2">
                  {translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.BY")}{" "}
                  <strong>{absence_proof.validator}</strong>
                </span>
              )}
            </div>

            <p>
              <strong className="me-2">
                {translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.SUBMITTED_ON")}:
              </strong>
              {formatDateHumanly(language, absence_proof.created_at, true)}
            </p>

            <div className="d-flex mb-3 align-items-center">
              <strong className="me-2">
                {translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.FILENAME")}:
              </strong>
              {absence_proof.filename}
              <a
                href={absence_proof.file_link}
                className="ms-2 text-decoration-none d-flex"
                target="_blank"
                rel="noreferrer"
              >
                <span className="dst-icon-link-external-02" />
              </a>
            </div>

            <div className="mb-3">
              <div className="me-2 fw-bold">
                {translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.COMMENT")}:
              </div>
              <div>{absence_proof.text}</div>
            </div>
          </div>
        ) : (
          <p>{translate(language, "PAGES.USER.MEETING.ABSENCE_PROOF_MODAL.NO_PROOF_PROVIDED")}</p>
        )}
      </Popup>
    </React.Fragment>
  );
};

export default MeetingBlock;
